import React from "react";
import ReactDOM from "react-dom/client";
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, LineElement, PointElement, ArcElement, Tooltip, Legend } from "chart.js";
import { Bar, Line, Doughnut } from "react-chartjs-2";

/** @typedef {{ [key: string]: { type: string, data: object, options: object } }} ReactObj */

ChartJS.register(CategoryScale, LinearScale, BarElement, LineElement, PointElement, ArcElement, Tooltip, Legend);

class RpaStat extends React.Component {
  /** @param {any} props */
  constructor(props) {
    super(props);

    this.startInputRef = React.createRef();
    this.endInputRef = React.createRef();
    this.startDatePickerRef = React.createRef();
    this.endDatePickerRef = React.createRef();

    this._onStartDateChange = this._onStartDateChange.bind(this);
    this._onEndDateChange = this._onEndDateChange.bind(this);

    this.state = {
      chartsObj: null,
      activeChart: "reservations",
      startDateStr: null,
      endDateStr: null
    };
  }

  async componentDidMount() {
    const chartsObj = await this.fetchChartsObj();
    this.setState(
      {
        chartsObj,
        startDateStr: chartsObj.startDateStr || null,
        endDateStr: chartsObj.endDateStr || null
      },
      () => {
        this._initDatepickers();
      }
    );
  }

  componentWillUnmount() {
    this._destroyDatepickers();
  }

  _getDatepickerOptions() {
    return {
      language: "fr",
      autohide: true,
      clearBtn: true,
      todayBtn: true,
      todayHighlight: true,
      todayBtnMode: 1,
      format: "dd/mm/yyyy"
    };
  }

  /**
   * @param {any} inputRef
   * @param {any} datePickerRef
   * @param {string|null} dateValue
   * @param {(event: any) => void} onChangeDate
   */
  _initOneDatepicker(inputRef, datePickerRef, dateValue, onChangeDate) {
    if (!inputRef.current) {
      return;
    }

    if (datePickerRef.current) {
      /** @type {any} */ (datePickerRef.current).destroy();
    }

    // @ts-ignore
    /** @type {any} */ (datePickerRef.current) = new Datepicker(inputRef.current, this._getDatepickerOptions());

    if (dateValue) {
      /** @type {HTMLInputElement} */ (inputRef.current).value = dateValue;
      /** @type {any} */ (datePickerRef.current).setDate(dateValue);
    }

    /** @type {HTMLInputElement} */ (inputRef.current).addEventListener("changeDate", onChangeDate);
  }

  _initDatepickers() {
    this._initOneDatepicker(this.startInputRef, this.startDatePickerRef, this.state.startDateStr, this._onStartDateChange);
    this._initOneDatepicker(this.endInputRef, this.endDatePickerRef, this.state.endDateStr, this._onEndDateChange);
  }

  _destroyDatepickers() {
    if (this.startInputRef.current) {
      /** @type {HTMLInputElement} */ (this.startInputRef.current).removeEventListener("changeDate", this._onStartDateChange);
    }

    if (this.endInputRef.current) {
      /** @type {HTMLInputElement} */ (this.endInputRef.current).removeEventListener("changeDate", this._onEndDateChange);
    }

    if (this.startDatePickerRef.current) {
      /** @type {any} */ (this.startDatePickerRef.current).destroy();
      this.startDatePickerRef.current = null;
    }

    if (this.endDatePickerRef.current) {
      /** @type {any} */ (this.endDatePickerRef.current).destroy();
      this.endDatePickerRef.current = null;
    }
  }

  async _refetchChartsObj() {
    const chartsObj = await this.fetchChartsObj();

    this.setState({ chartsObj });
  }

  /** @param {any} event */
  _onStartDateChange(event) {
    const nextStartDateStr = event?.target?.value || null;

    if (nextStartDateStr === this.state.startDateStr) {
      return;
    }

    this.setState(
      {
        startDateStr: nextStartDateStr
      },
      () => {
        this._refetchChartsObj();
      }
    );
  }

  /** @param {any} event */
  _onEndDateChange(event) {
    const nextEndDateStr = event?.target?.value || null;

    if (nextEndDateStr === this.state.endDateStr) {
      return;
    }

    this.setState(
      {
        endDateStr: nextEndDateStr
      },
      () => {
        this._refetchChartsObj();
      }
    );
  }

  /**
   * Chargement des dates déjà prises via l'api
   * @returns {Promise<ReactObj>} La liste des dates déjà prises
   */
  async fetchChartsObj() {
    const queryParams = new URLSearchParams();

    if (this.state.startDateStr) {
      queryParams.set("start_date", this.state.startDateStr);
    }

    if (this.state.endDateStr) {
      queryParams.set("end_date", this.state.endDateStr);
    }

    const queryString = queryParams.toString();

    const url = queryString ? `/api/rpa-stat/charts?${queryString}` : "/api/rpa-stat/charts";

    var response = await fetch(url, {
      headers: {
        Authorization: "gestiaweb"
      }
    });

    return await response.json();
  }

  /** @param {string} typeApi */
  renderChart(typeApi) {
    const { chartsObj } = this.state;

    if (!chartsObj) return <div>Loading...</div>;

    const chartObj = chartsObj[typeApi];

    const { type, data, options } = chartObj;

    const key = `${typeApi}-${this.state.activeChart}`;

    switch (type) {
      case "bar":
        return <Bar data={data} options={options} key={key} />;
      case "line":
        return <Line data={data} options={options} key={key} />;
      case "doughnut":
        return <Doughnut data={data} options={options} key={key} />;
      default:
        return <div>Type non supporté</div>;
    }
  }

  _onClickOnButtonGroup(chartType) {
    this.setState({ activeChart: chartType });
  }

  render() {
    return (
      <div style={{ display: "grid", height: "100%", gridTemplateRows: "auto 1fr", gridTemplateColumns: "1fr", gap: "8px" }}>
        <div style={{ display: "grid", gridTemplateColumns: "180px 180px max-content", gridTemplateRows: "1fr", alignItems: "start", gap: "8px" }}>
          <input
            className="input-group__input"
            // @ts-ignore
            ref={this.startInputRef}
            aria-label="Date de début"
            placeholder="Date de début"
          />
          <input
            className="input-group__input"
            // @ts-ignore
            ref={this.endInputRef}
            aria-label="Date de fin"
            placeholder="Date de fin"
          />

          <div className="buttongroup">
            <button className={this.state.activeChart === "reservations" ? "buttongroup__button buttongroup__button--pushed" : "buttongroup__button"} onClick={this._onClickOnButtonGroup.bind(this, "reservations")}>
              Réservations par activités/resources
            </button>
            <button className={this.state.activeChart === "frequentations" ? "buttongroup__button buttongroup__button--pushed" : "buttongroup__button"} onClick={this._onClickOnButtonGroup.bind(this, "frequentations")}>
              Fréquentations par activités/resources
            </button>
            <button className={this.state.activeChart === "services" ? "buttongroup__button buttongroup__button--pushed" : "buttongroup__button"} onClick={this._onClickOnButtonGroup.bind(this, "services")}>
              Services
            </button>
            <button className={this.state.activeChart === "gender" ? "buttongroup__button buttongroup__button--pushed" : "buttongroup__button"} onClick={this._onClickOnButtonGroup.bind(this, "gender")}>
              Par genre
            </button>
            <button className={this.state.activeChart === "day" ? "buttongroup__button buttongroup__button--pushed" : "buttongroup__button"} onClick={this._onClickOnButtonGroup.bind(this, "day")}>
              Par jour
            </button>
          </div>
        </div>
        <div>
          {this.state.activeChart === "reservations" ? (
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px", height: "100%", gridTemplateRows: "1fr" }}>
              <div style={{ height: "100%" }}>{this.renderChart("reservationPerResource")}</div>
              <div style={{ height: "100%" }}>{this.renderChart("reservationPerActivity")}</div>
            </div>
          ) : this.state.activeChart === "frequentations" ? (
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px", height: "100%", gridTemplateRows: "1fr" }}>
              <div style={{ height: "100%" }}>{this.renderChart("rpaVisitsPerResource")}</div>
              <div style={{ height: "100%" }}>{this.renderChart("rpaVisitsPerActivity")}</div>
            </div>
          ) : this.state.activeChart === "services" ? (
            <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: "8px", height: "100%", gridTemplateRows: "1fr" }}>
              <div style={{ height: "100%" }}>{this.renderChart("rpaQuantityPerUnitService")}</div>
            </div>
          ) : this.state.activeChart === "gender" ? (
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px", height: "100%", gridTemplateRows: "1fr" }}>
              <div style={{ height: "100%" }}>{this.renderChart("rpaReservationsPerGender")}</div>
              <div style={{ height: "100%" }}>{this.renderChart("rpaReservationsPerGenderAndActivity")}</div>
            </div>
          ) : this.state.activeChart === "day" ? (
            <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: "8px", height: "100%", gridTemplateRows: "1fr" }}>
              <div style={{ height: "100%" }}>{this.renderChart("rpaReservationsAndVisitsPerDay")}</div>
            </div>
          ) : null}
        </div>
      </div>
    );
  }
}

const rpaStatNode = document.getElementById("rpa-stat");

if (rpaStatNode) {
  const root = ReactDOM.createRoot(rpaStatNode);

  root.render(<RpaStat />);
}
